<template>
  <v-dialog
    v-model="localDialog"
    width="50%"
    @update:modelValue="updateDialog"
    transition=""
    persistent
    :open-delay="0"
    :close-delay="0"
  >
    <v-card>
      <v-card-title class="text-h6">
        <span>{{ isEntryMode ? "Add Budget Revision" : "Update Budget Revision" }}</span>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <v-form @submit.prevent="submit" ref="BudgetRevisionSetup">
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="project_budget_id.value.value"
                :error-messages="project_budget_id.errorMessage.value"
                clearable
                :items="getProjectBudgetList"
                :item-title="item => item ? `${item.id} - ${item.project?.project_title || item.project?.name || 'Unknown'} (${item.project?.code || 'N/A'})` : ''"
                autocomplete="off"
                item-value="id"
                @update:model-value="onProjectBudgetChange"
              >
                <template #label>
                  Project Budget ID <span class="text-red-accent-4">*</span>
                </template>
              </v-autocomplete>
              <v-text-field
                v-model="revision_no.value.value"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                :error-messages="revision_no.errorMessage.value"
                clearable
              >
                <template #label>
                  Revision No <span class="text-red-accent-4">*</span>
                </template>
              </v-text-field>



              <v-text-field
                v-model="current_budget_amount.value.value"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                :error-messages="current_budget_amount.errorMessage.value"
                readonly
              >
                <template #label>
                  Project Total Budget (Current)
                </template>
              </v-text-field>

              <v-text-field
                v-model="new_budget_amount.value.value"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                type="number"
                step="0.01"
                :error-messages="new_budget_amount.errorMessage.value"
                clearable
              >
                <template #label>
                  Project Total Budget (New) <span class="text-red-accent-4">*</span>
                </template>
              </v-text-field>

              <v-date-input
                v-model="revision_date.value.value"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                autocomplete="off"
                clearable
                :error-messages="revision_date.errorMessage.value"
              >
                <template #label>
                  Revision Date <span class="text-red-accent-4">*</span>
                </template>
              </v-date-input>

              <v-select
                v-model="revision_status.value.value"
                :items="revisionStatusOptions"
                :error-messages="revision_status.errorMessage.value"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                item-title="text"
                item-value="value"
              >
                <template #label>
                  Revision Status 
                </template>
              </v-select>

              <v-checkbox
                label="Active"
                v-model="status.value.value"
                :error-messages="status.errorMessage.value"
                density="compact"
                variant="outlined"
                hide-details
                single-line
                color="primary"
                value="1"
              ></v-checkbox>
              <input v-model="budget_revision_id.value.value" type="hidden" />
            </v-col>
          </v-row>
          <v-divider></v-divider>
          <v-card-actions>
            <v-layout class="justify-end">
              <v-btn
                class="bg-red"
                variant="flat"
                type="reset"
                @click="closeDialog"
                >Close</v-btn
              >
              <v-btn class="bg-brand w-15 ms-2" variant="flat" type="submit">
                {{ isEntryMode ? "Save" : "Update" }}
              </v-btn>
            </v-layout>
          </v-card-actions>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, watch } from "vue";
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";

export default {
  name: "BudgetRevisionSetup",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({
        revision_no: "",
        project_budget_id: "",
        current_budget_amount: "",
        new_budget_amount: "",
        revision_date: "",
        revision_status: "draft",
        status: "1",
        revision_id: null,
      }),
    },
  },
  data() {
    return {
      localDialog: this.dialog,
      selectedImage: null,
      errorMessages: "",
      loading: false,
      isEntryMode: true,
      revisionStatusOptions: [
        { text: "Draft", value: "draft" },
        { text: "Approved", value: "approved" },
      ],
    };
  },
  computed: {
    getProjectBudgetList() {
      return this.$store.getters.getProjectBudgetList;
    },
  },
  watch: {
    dialog(val) {
      this.localDialog = val;
    },
    localDialog(val) {
      this.$emit("update:dialog", val);
    },
    data: {
      handler(newData) {
        this.isEntryMode = !newData.id;
        this.resetForm({ errors: {}, touched: {} });
        this.revision_no.setValue(newData.revision_no || "", false);
        this.project_budget_id.setValue(newData.budget_id || "", false); // Map budget_id to project_budget_id
        this.current_budget_amount.setValue(newData.project_total_budget_old || "", false); // Map API field
        this.new_budget_amount.setValue(newData.project_total_budget_new || "", false); 
        this.revision_date.setValue(newData.revision_date || "", false);
        this.revision_status.setValue(newData.revision_status || "draft", false);
        this.status.setValue(String(newData.status) || "1", false);
        this.budget_revision_id.setValue(newData.id || "", false);
      },
    },
  },
  created() {
    if (this.$store.getters.getProjectBudgetList.length === 0) {
      this.$store.dispatch("getProjectBudgetList");
    }
  },
  methods: {
    updateDialog(val) {
      this.$emit("update:dialog", val);
    },
    closeDialog() {
      this.$emit("update:dialog", false);
    },
    onProjectBudgetChange(budgetId) {
      if (!budgetId) {
        this.current_budget_amount.value.value = "";
        return;
      }
      const budgetList = this.getProjectBudgetList || [];
      const selectedBudget = budgetList.find(budget => budget.id == budgetId);
      if (selectedBudget) {
        this.current_budget_amount.value.value = selectedBudget.project_total_budget;
      } else {
        this.current_budget_amount.value.value = "";
      }
    },
  },
  setup(props, { emit }) {
    const { handleSubmit, resetForm } = useForm({
      validationSchema: yup.object({
        revision_no: yup.string().required().label("Revision No"),
        project_budget_id: yup.number().required().label("Project Budget ID"),
        new_budget_amount: yup.number().required().min(0).label("Project Total Budget (New)"),
        revision_date: yup.string().required().label("Revision Date"),
        revision_status: yup.string().required().oneOf(["draft", "approved"]).label("Revision Status"),
        status: yup.string().nullable(),
      }),
    });

    const revision_no = useField("revision_no");
    const project_budget_id = useField("project_budget_id");
    const current_budget_amount = useField("current_budget_amount");
    const new_budget_amount = useField("new_budget_amount");
    const revision_date = useField("revision_date");
    const revision_status = useField("revision_status");
    const status = useField("status");
    const budget_revision_id = useField("budget_revision_id");
    const errorMessages = ref("");
    const loading = ref("");
    const store = useStore();

    const submit = handleSubmit((values) => {
 
  const transformedData = {
    budget_id: values.project_budget_id,
    revision_no: values.revision_no,
    revision_date: values.revision_date,
    project_total_budget_old: values.current_budget_amount,
    project_total_budget_new: values.new_budget_amount,
    approval_status: values.revision_status,
    revision_status: values.revision_status,
    status: values.status === "1" || values.status === 1 ? "1" : "0",
    budget_revision_master_id: values.budget_revision_id || null
  };
  
  store
    .dispatch("saveUpdateProjectBudgetRevision", transformedData)
        .then((response) => {
          resetForm();
          emit("update:dialog", false);
          toast.success(response.data.message);
          loading.value = true;
          store
            .dispatch("getProjectBudgetRevisionList")
            .then((data) => {
              loading.value = false;
            })
            .catch((error) => {
              loading.value = false;
              toast.error(error.response.data.message);
            });
        })
        .catch((error) => {
          toast.error(error.response.data.message);
        });
    });
    return {
      submit,
      revision_no,
      project_budget_id,
      current_budget_amount,
      new_budget_amount,
      revision_date,
      revision_status,
      status,
      errorMessages,
      budget_revision_id,
      resetForm,
      loading,
    };
  },
  mounted() {},
};
</script>